import { Link } from 'react-router-dom';
import { useUserQuotes } from '@/hooks/useQuotesQuery';
import { useCartStore } from '@/stores/cartStore';
import { fx } from '@/lib/currency';
import type { Quote, QuoteStatus } from '@/types/Quote';

function statusBadge(status: QuoteStatus): { className: string; label: string } {
  switch (status) {
    case 'draft':
      return { className: 'live', label: 'Draft' };
    case 'sent':
      return { className: 'oem', label: 'Sent' };
    case 'accepted':
      return { className: 'live', label: 'Accepted' };
    case 'rejected':
      return { className: 'sold', label: 'Rejected' };
    case 'closed':
      return { className: 'sold', label: 'Closed' };
  }
}

function formatDate(q: Quote): string {
  if (q.createdAt?.toDate) {
    return q.createdAt.toDate().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  return '—';
}

export default function Orders() {
  const { data: quotes, isLoading, error } = useUserQuotes();
  const addLine = useCartStore((s) => s.addLine);

  function reorder(q: Quote) {
    q.lineItems.forEach((li) =>
      addLine({
        partId: li.partId,
        qty: li.qty,
        unitPrice: li.unitPrice,
        shippingWeight: li.shippingWeight,
        snapshot: {
          title: li.partSnapshot.title,
          sku: li.partSnapshot.sku,
          ...(li.partSnapshot.casting !== undefined ? { casting: li.partSnapshot.casting } : {}),
          iconId: li.partSnapshot.iconId,
        },
      })
    );
  }

  return (
    <div className="app">
      <div className="main">
        <div className="eyebrow">Quotes & Orders</div>
        <h1 className="h-title">My Quotes / Orders</h1>
        <p className="lead" style={{ marginBottom: 28 }}>
          Active quotes, past orders, and downloadable PDFs. Drafts can be reopened to keep
          editing; sent quotes are read-only and re-orderable.
        </p>

        {isLoading && <div className="empty-state">Loading quotes…</div>}
        {error && (
          <div className="auth-error">
            Failed to load quotes: {error instanceof Error ? error.message : String(error)}
          </div>
        )}

        {!isLoading && (quotes?.length ?? 0) === 0 && (
          <div
            className="empty-state"
            style={{ background: 'var(--bg-1)', border: '1px dashed var(--line-2)', padding: 60 }}
          >
            <div style={{ fontFamily: 'var(--display)', fontSize: 28, color: 'var(--text-dim)' }}>
              No quotes yet.
            </div>
            <p style={{ margin: '12px 0 18px' }}>
              Build a quote from the inventory and save the draft to see it here.
            </p>
            <Link to="/inventory" className="btn">Browse Inventory →</Link>
          </div>
        )}

        {quotes && quotes.length > 0 && (
          <div className="table-scroll">
          <table className="admin-table cards-on-mobile">
            <thead>
              <tr>
                <th>Quote #</th>
                <th>Customer / Project</th>
                <th>Date</th>
                <th style={{ textAlign: 'right' }}>Items</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => {
                const badge = statusBadge(q.status);
                return (
                  <tr key={q.id}>
                    <td data-label="Quote" className="sku">{q.number}</td>
                    <td data-label="Customer">
                      <span>
                        <strong style={{ color: 'var(--cream)' }}>{q.customer.name}</strong>
                        {q.customer.project && (
                          <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 2 }}>
                            {q.customer.project}
                          </div>
                        )}
                      </span>
                    </td>
                    <td data-label="Date">{formatDate(q)}</td>
                    <td data-label="Items" style={{ textAlign: 'right' }}>{q.lineItems.length}</td>
                    <td data-label="Total" className="price" style={{ textAlign: 'right' }}>
                      {fx(q.total, q.currency)}
                    </td>
                    <td data-label="Status">
                      <span className={`badge ${badge.className}`}>{badge.label}</span>
                    </td>
                    <td className="actions">
                      <Link to={`/quote/pdf?id=${encodeURIComponent(q.id)}`} className="btn sm dark">
                        PDF
                      </Link>
                      <button type="button" className="btn sm dark" onClick={() => reorder(q)}>
                        Reorder
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
